import { formatBytes, formatCents } from './general.ts'
import type { PlanRow } from '../models/useBilling'

/**
 * The lines on a plan card (app#136 item 1, app#199, app#219).
 *
 * ⚠⚠ THE CARD IS A COMPARISON SURFACE AND PROSE DEFEATS IT (app#219). Benoit, 2026-09-03: "we
 * need to focus on the important aspects of each plan and show the simplicity of our pricing."
 * The model really is simple — one flat price per organization, two meters (bandwidth and
 * provisioned compute), and everything else a plan LIMIT rather than a charge — and five to nine
 * lines of equal weight per card hid it. So each function here returns a VALUE for a labelled
 * row, not a sentence, and every card renders the same rows in the same order. Three numbers
 * differ between the columns; the shape does not.
 *
 * ⚠⚠ CONCISE IS THE GOAL AND A LOST FACT IS A REGRESSION. Several of the sentences this replaced
 * were long because a shorter version was measurably wrong, each filed as its own issue. What
 * stays on the card, and why:
 *
 *   app#147 — Free STOPS, paid SLOWS. The single most consequential difference between the
 *             columns, and a customer must not learn it after the fact. Both words are in
 *             `overageLine`, one per regime.
 *   app#160 — one sentence cannot cover two allowances whose consequences differ. Separate ROWS
 *             split the two ALLOWANCES, but the consequence row sits under BOTH of them, so
 *             `OVERAGE_LABEL` has to name the meter it speaks for. Rows alone are not the split.
 *   app#163 — "nodes stay online" is TRUE for bandwidth and false for compute. It rides on the
 *             bandwidth regime's own line, under a label that says bandwidth, and nowhere else.
 *   app#136 — storage is a LIMIT, bandwidth is a billable ALLOWANCE. ⚠ The word LEFT the label on
 *             2026-09-03: it is "Storage" now, and the fact is `PRICING_MODEL`'s clause
 *             "everything else is a plan limit, not a charge", stated once for all four cards.
 *             See `planRows` for why it moved and `PRICING_MODEL` for what carries it — do not
 *             put "limit" back on the label without taking the clause out, or one fact is two
 *             strings that can come to disagree.
 *
 * ⚠ WHAT DELIBERATELY LEFT, to `docs.nobgp.com/plans-and-billing`, which the panel now links:
 * the compute CLASS rates, and what a stopped machine keeps and loses (app#155, app#159). Those
 * are explanations, and the card carries facts. ⚠ The app#159 pair is still ONE string —
 * `KEEPS_BUT_NOT_DISK` in `computeStop.ts` — and the banners still render it; the card is no
 * longer one of its readers.
 *
 * ⚠ Nothing here is derived from a plan NAME. Every branch reads `metered` / `hardCaps` / the
 * columns, so a renamed or newly-added plan reads correctly — the rule `isFreePlan` follows.
 *
 * ⚠ The exempt tier answers 'Custom' to every row. It is `metered: false`, never a name.
 */

/** The quote-only tier's answer to every number. */
const CUSTOM = 'Custom'

/**
 * A CATALOG size, with no trailing `.0`.
 *
 * ⚠ NOT a change to `formatBytes`, deliberately, and the two callers are not the same kind of
 * number. A MEASURED figure keeps its decimal — the usage bars read "37.4 GB of 50.0 GB" and the
 * tenth is information there. A catalog allowance is round by construction, so its ".0" is width
 * and nothing else, and width is what wrapped these rows at four cards across (measured: the
 * value column is 73 px and "100.0 GB / mo" wants 78).
 */
const catalogBytes = (bytes: number): string => formatBytes(bytes).replace('.0 ', ' ')

/**
 * The "from" rate on the plan card: the lowest non-zero hourly price among OFFERED machine classes,
 * in cents, or null when there is none to read (app#331).
 *
 * ⚠⚠ READ FROM `compute_classes`, NEVER TYPED HERE. The usage table prints each class rate from the
 * same table, through its RPC (router migration 0111), so a re-priced class reaches both panels on
 * the next read. A
 * copy of the `small` rate in this file was the one number on the card that did not come from the
 * database.
 *
 * ⚠ Only OFFERED classes: a retired class is not a price a customer can buy today. A zero price is
 * not a rate either, so it is skipped. `offered` is read as `!== false`, the `PlanRow.offered` rule.
 */
export const lowestComputeRate = (
  classes: readonly { offered?: boolean | null; priceCentsPerHour: number | null }[],
): number | null => {
  const rates = classes
    .filter(c => c.offered !== false)
    .map(c => c.priceCentsPerHour)
    .filter((r): r is number => typeof r === 'number' && Number.isFinite(r) && r > 0)
  return rates.length > 0 ? Math.min(...rates) : null
}

/**
 * THE MODEL, STATED ONCE FOR ALL FOUR COLUMNS (app#219).
 *
 * ⚠⚠ IT LIVES HERE RATHER THAN IN `Billing.tsx` BECAUSE IT CARRIES A FACT THAT USED TO BE ON A
 * ROW, and a fact held in JSX is a fact no test can reach — the same argument that put the row
 * LABELS in `planRows`. app#136's fact moved here: storage is a LIMIT and bandwidth is a billable
 * ALLOWANCE, carried by "everything else is a plan limit, not a charge". It was the word "limit"
 * in the storage row's label, which made that label the widest on the card and took 11 px from
 * every value in all four columns.
 *
 * ⚠⚠ THE COMPUTE UNIT IS NOT HERE. It was a footnote under the picker (Benoit, 2026-09-05: "use
 * hours, no need to specify 'of small compute', just add a single footnote") until app#331 moved
 * the card to dollars, which need no footnote. This sentence sits above the picker and is read by
 * everyone opening the panel, so a clause defining a unit competed with the model it exists to
 * state. ⚠ Do not put a unit into this sentence: the compute row states its own amount in dollars.
 *
 * ⚠⚠ EVERY SENTENCE HERE MUST BE TRUE OF EVERY COLUMN, AND ONE WAS NOT (app#236). It ended
 * "Overage is paid from usage credit, so your bill is the flat price until you buy credit or turn
 * on auto-refill" — true of Pro and Business, FALSE of Free, which is `hard_caps`: transfers stop,
 * there is no overage, and `showsCredit` keeps `CreditBlock`'s controls off the page (since
 * app#265 a Free org that came down from a paid plan sees its balance there, read-only). So it named
 * two controls a Free reader cannot see, above a card saying "never a bill". Benoit cut it,
 * 2026-09-03.
 *
 * ⚠⚠ IT WAS THE FOURTH INSTANCE OF ONE CLASS — money copy that does not read the plan (app#141,
 * app#147, app#156, app#158, all closed and each fixed where it was found). The mechanism is that
 * this constant takes NO PLAN and so cannot branch the way `overageLine` and `unlimitedLine` do,
 * which is why a reword would have closed the instance and left the class. The guard in
 * `planLines.test.ts` is what closes it: no paid-only control may be NAMED here, so the fifth
 * attempt to add one fails a test instead of reaching a customer.
 *
 * ⚠ THE FACT IS NOT LOST, it is plan-derived now. Each paid card's own overage row says
 * "$0.12/GB from usage credit — transfers slow without it", and Free's says "never a bill" —
 * `overageLine`, branching on `hardCaps`. That is where a sentence about one plan's money belongs.
 *
 * ⚠⚠ MIGRATION `0153` IS WHY THIS WAS NOT A MINORITY CASE. It puts EVERY organization back on Free
 * with an empty credit balance at the Stripe switch (router#533 act 4), so the cut sentence would
 * have been wrong for the whole customer base at once, on the day we first take money.
 */
export const PRICING_MODEL =
  'One flat price per organization, plus two meters: bandwidth and provisioned compute. ' +
  'Everything else is a plan limit, not a charge.'

/** METER ONE. What the plan includes each period and may be BILLED past (paid) or STOPPED at (Free). */
export const bandwidthLine = (plan: PlanRow): string =>
  !plan.metered ? CUSTOM : `${catalogBytes(plan.bandwidthBytesFree)} / mo`

/**
 * A CATALOG amount of money, with no trailing `.00` — `$5`, not `$5.00`.
 *
 * ⚠ The same reason `catalogBytes` drops `.0`: a catalog allowance is round by construction, so
 * the cents are width and nothing else. A figure that is not round keeps them (`$2.50`).
 */
const catalogMoney = (cents: number): string => formatCents(cents).replace(/\.00$/, '')

/**
 * METER TWO, in dollars (app#331, Benoit 2026-09-14): "$5 of compute included, then from $0.02/h".
 *
 * ⚠⚠ THE CARD SELLS MONEY NOW, NOT SMALL-MACHINE HOURS. The allowance is one dollar pool shared by
 * every machine size (`plans.compute_allowance_cents`, and `usageAxis.ts` carries why). In hours
 * the card had to divide by the `small` rate and needed a footnote to say which hour; a medium
 * machine then read as three hours an hour. In dollars the row is the column's own value.
 *
 * ⚠⚠ THE NUMBER IS READ FROM THE PLAN ROW, NEVER WRITTEN HERE (app#199). One hardcoded string
 * served both paid cards once, and Business printed Pro's figure. `planLines.test.ts` states the
 * catalogue as data (Free $1, Pro $5, Business $50 after router#1464), so a changed allowance
 * cannot keep an old string.
 *
 * ⚠ The row is SHORT on purpose: at four cards across the value column has about 84 px. The rest
 * of the sentence — "then from $0.02/h" — is `computeDetail`, rendered under the row
 * across the full card width.
 *
 * ⚠⚠ A ZERO ALLOWANCE IS A THIRD STATE AND IT IS NOT "Custom". The exempt tier reads `Custom`
 * because it is `metered: false`. A METERED plan carrying 0 has a list price and a real allowance
 * of zero, so it reads `None`.
 */
export const computeLine = (plan: PlanRow): string => {
  const cents = plan.computeAllowanceCents
  // ⚠ `Number.isFinite`: the row arrives from `select('*')`, so a catalog that predates the column
  // hands over `undefined`. An unknown allowance is the one case with no honest number.
  if (!plan.metered || !Number.isFinite(cents)) return CUSTOM
  if (cents <= 0) return 'None'
  return `${catalogMoney(cents)} / mo`
}

/**
 * The second half of the compute row: what the allowance is and what happens past it (app#331).
 *
 * ⚠⚠ "then from $0.02/h" ON A PAID PLAN, AND THE WORD "THEN" IS THE POINT. The included
 * amount is not a ceiling: compute continues past it, paid from credit at the list rate. A card
 * reading "$5 / mo" alone reads as "$20 buys at most $5 of compute" (app#327's concern).
 *
 * ⚠ "from" because the rate is the CHEAPEST offered size's (`lowestComputeRate`); larger sizes
 * cost more per hour, and the documentation page linked above the picker lists them. When the
 * rate is unknown (`null`), a paid card prints no "then from" part, and the line is a
 * non-breaking space like the exempt tier's.
 *
 * ⚠⚠ FREE SAYS "never a bill" AND NOTHING ABOUT STOPPING. A `hard_caps` plan bills no overage,
 * which is a promise the card already makes for bandwidth. What happens to a Free machine past
 * the allowance depends on the router's enforcement switch, which the app cannot read, so the card
 * does not state it (app#160, `computeStop.ts`).
 *
 * ⚠⚠ SHORT, BECAUSE A WRAP BREAKS THE ROW ALIGNMENT ACROSS THE FOUR CARDS. The first copy was
 * "included, then from $0.02/h": 145 px in the harness against a 151 px column, and it wrapped on
 * the live preview at a 1524 px window (the real dialog is narrower than the harness). "then from
 * $0.02/h" is 94 px. The row label and the "/ mo" value already say it is the monthly amount.
 *
 * ⚠ The exempt tier gets a non-breaking space, not an empty string, so its card keeps the same
 * line count and the rows of the four cards stay level.
 */
export const computeDetail = (plan: PlanRow, centsPerHour: number | null): string => {
  if (!plan.metered || !Number.isFinite(plan.computeAllowanceCents)) return '\u00a0'
  if (plan.hardCaps) return 'never a bill'
  // ⚠ No rate, no number: a failed or unloaded catalogue read must not print a guessed price.
  if (centsPerHour === null) return '\u00a0'
  const rate = `from ${formatCents(centsPerHour)}/h`
  return plan.computeAllowanceCents > 0 ? `then ${rate}` : rate
}

/**
 * NOT A METER — a cap, on every plan.
 *
 * ⚠ There is no storage overage rate on any plan (migration 0108 dropped the column), so passing
 * it is not a bigger bill — it is a refused upload. ⚠⚠ THAT FACT MOVED OFF THE LABEL AND INTO
 * `PRICING_MODEL` — see `planRows`. The label is "Storage" now, and the panel says once, above
 * all four cards, that everything besides the two meters is a plan limit and not a charge.
 */
export const storageLine = (plan: PlanRow): string =>
  !plan.metered ? CUSTOM : catalogBytes(plan.storageBytesFree)

/**
 * The three rows every card carries, in one order: the TWO METERS first, then the one limit.
 *
 * ⚠⚠ THE LABEL IS "Storage" AND app#136's FACT NOW LIVES IN `PRICING_MODEL` (Benoit, 2026-09-03).
 * app#136 was that a bare "100 GB" reads as an included amount you may exceed and be billed for,
 * and the remedy then was the word "limit" in this label. That remedy cost the WIDEST label on
 * the card — 67 px against `Bandwidth`'s 56 — and the label column is set by its widest member,
 * so it took 11 px from every value in every card. `PRICING_MODEL` says "everything else is a
 * plan limit, not a charge" ONCE, above all four columns, which is the same fact stated for four
 * cards instead of one row. `planLines.test.ts` asserts the sentence, so the fact is still one a
 * test can reach — that was the reason the labels live here rather than in JSX, and it holds.
 *
 * ⚠ The order is the model's own order, so the eye meets the two things that are metered before
 * the thing that is not.
 *
 * `centsPerHour` is the compute "from" rate, `lowestComputeRate` over `compute_classes`, or null.
 */
export const planRows = (
  plan: PlanRow,
  centsPerHour: number | null,
): { label: string; value: string; detail?: string }[] => [
  { label: 'Bandwidth', value: bandwidthLine(plan) },
  // ⚠ `detail` is rendered under the row across the full card width (app#331). The value column
  // is too narrow for "then from $0.02/h", and the label column must stay set by
  // `Bandwidth`.
  { label: 'Compute', value: computeLine(plan), detail: computeDetail(plan, centsPerHour) },
  { label: 'Storage', value: storageLine(plan) },
]

/**
 * The headline (app#219).
 *
 * ⚠⚠ THIS IS THE STRONGEST THING WE SELL AND IT WAS A FOOTNOTE IN THE MIDDLE OF A LIST. Benoit
 * asked for it to read as a headline, so the component renders it across both columns in a
 * heavier weight. Its words belong here anyway, because the devices half is CONDITIONAL.
 *
 * ⚠ `0` means UNLIMITED on `node_limit` and not "none" — the load-bearing convention
 * `CheckNodeLimit` uses. Migration 0119 set Free's to 0, so no offered plan caps devices; the
 * column survives as the lever that bounds ONE abusive org (router#948), which is why the
 * non-zero branch stays. Reading it as zero would advertise a plan with no devices.
 *
 * ⚠ The exempt tier claims nothing about devices — they are quoted, like everything else on that
 * card — but users, networks and services are unlimited on every plan we sell.
 */
export const unlimitedLine = (plan: PlanRow): string => {
  const rest = 'users, networks and services'
  if (!plan.metered) return `Unlimited ${rest}`
  if (plan.nodeLimit === 0) return `Unlimited devices, ${rest}`
  return `Up to ${plan.nodeLimit} devices · unlimited ${rest}`
}

/**
 * ONE compact line for what happens past the BANDWIDTH allowance — and the one row where the two
 * regimes must stay legibly different (app#147). ⚠ It speaks for one meter only, and
 * `OVERAGE_LABEL` is what says so on the card.
 *
 * ⚠⚠ FREE STOPS AND PAID SLOWS, AND THEY ARE NOT DEGREES OF ONE THING. nobgp-router#966 (router
 * v0.4.106) split them deliberately: a PAID org past its allowance with nothing to pay from is
 * THROTTLED, because the overage is billed and the throttle brakes a bill somebody agreed to; a
 * FREE org is BLOCKED, because there is no bill to brake. The card said "throttled" of Free while
 * the banner and the Usage alert in this same app said "stopped" (app#147). This is the single
 * most consequential difference between the columns and a customer must not learn it after the
 * fact, so both words stay on the card even though everything around them went to the docs page.
 *
 * ⚠ "nodes stay online" IS LOAD-BEARING AND IS KEPT (app#163). A bandwidth block acts on the
 * billable data path and never touches the bridge, so the whole fleet stays connected and only
 * the transfers stop. `docs/PRICING.md` asserts it, the router's own alert copy asserts it, and
 * `computeStop.ts` warns against sweeping the phrase away. It belongs to the BANDWIDTH regime
 * only — never widen it to cover compute, which is app#160.
 *
 * ⚠⚠ "never a bill" IS A PROMISE, NOT AN EXPLANATION, so it stays while the explanations go.
 * A Free org cannot be billed for overage at all.
 *
 * ⚠ THE PAID LINE NAMES THE CREDIT BALANCE AND NOT A SPEND CAP (app#203). nobgp-router#1195
 * narrowed the cap to bound AUTOMATIC REFILLS and nothing else, so "your spend cap starts at $0"
 * sent a reader looking for a control that does something else. The rest of that mechanism — the
 * refill rule, the monthly cap, what the balance is spent on — is `CreditBlock`, rendered two
 * inches below this picker, and the documentation link above it. It is not re-explained per card.
 *
 * ⚠ A paid plan with a zero bandwidth rate still slows, so the fact survives the missing number
 * rather than the row disappearing with it.
 *
 * ⚠⚠ THE WORD "SUSPENDED" MUST NOT COME BACK (nobgp-router#1078). Nothing here says it.
 */
export const overageLine = (plan: PlanRow): string => {
  if (!plan.metered) return CUSTOM
  if (plan.hardCaps) return 'Nodes stay online, transfers stop — never a bill'
  return plan.bandwidthRateCentsPerGb > 0
    ? `${formatCents(plan.bandwidthRateCentsPerGb)}/GB from usage credit — transfers slow without it`
    : 'Transfers slow until usage credit covers them'
}

/**
 * The label the overage row carries. Here for the same reason `planRows` holds the others.
 *
 * ⚠⚠ IT NAMES THE METER, AND THAT IS app#160 AND app#163 (fix them in the LABEL or they come
 * back). The card carries TWO allowance rows — Bandwidth and Compute — so a bare "Past the
 * allowance" under both of them reads as covering both, and the sentence beside it is TRUE of
 * bandwidth and FALSE of compute: past the COMPUTE allowance a Free org's machine is stopped and
 * that node goes OFFLINE, so "Nodes stay online" is the wrong statement on the wrong meter.
 * Separate rows split the ALLOWANCES; only this word splits the CONSEQUENCE. What happens past
 * the compute allowance is `docs.nobgp.com/plans-and-billing`, which the panel links.
 */
export const OVERAGE_LABEL = 'Past the bandwidth allowance'
